import React, { useCallback, useState, useEffect } from 'react';
import { Modal } from '@sabre/spark-react-core';
import { bool, object } from 'prop-types';
import { CSVLink } from 'react-csv';
import { useCorePricingContext } from '../../contexts/core-pricing-context.jsx';
import {
   URL_CORE_PRICING_NEW_FARES_SAVE,
   URL_DYNAMIC_RESOLUTION,
   URL_CONSTRUCTED_FARES,
   URL_NEW_DISTRIBUTION_GROUP_GENERATE,
   URL_NEW_DISTRIBUTION_GROUP_SEARCH,
} from '../../end-points.js';
import { useCreateApi } from '../../hooks/use-create-api.js';
import { useFetch } from '../../hooks/use-fetch.js';
import { useSave } from '../../hooks/use-save.js';
import useSearch from '../../hooks/use-search.js';
import AgGridReactTable from '../../ui-components/ag-gird-table/ag-grid-react-table.jsx';
import DistributionDeleteConfirmation from '../../ui-components/core-pricing-component/distribution-delete-confirmation-modal/distribution-delete-confirmation-modal.jsx';
import NewDistributionGroup from '../../ui-components/core-pricing-component/new-distribution-group/new-distribution-group.jsx';
import Loader from '../../ui-components/loader/loader.jsx';
import RecordCount from '../../ui-components/record-count/record-count.jsx';
import { getHostCarrierCode } from '../../utils/common-util.js';
import {
   API_RESPONSE_KEY_NAMES,
   API_STATUS,
   ERROR_MESSAGE_LIMIT,
   FARE_STATUS,
   SCREEN_NAMES,
   SIZE,
   USER_ACTION_CODE_FARES_SCREEN,
} from '../../utils/constants';
import { DateUtil } from '../../utils/date-util.js';
import { NOTIFICATION_MESSAGES } from '../../utils/notification-messages.js';
import { toastSuccess, toastWarn } from '../../utils/toasts.js';
import {
   cancelFareRecords,
   contextMenuConfig,
   createSaveFarePayload,
   getRowClasses,
   getUserActionCode,
} from './__helpers__/functions.js';
import {
   specifiedFaresTableHeaders,
   constructedFaresTableHeaders,
} from './__helpers__/tableHeaders';

const FaresResultScreen = ({
   specifiedFaresSearchQuery,
   constructedFaresSearchQuery,
   abortSearch,
   productType,
}) => {
   const [selectedRows, setSelectedRows] = useState(['1']);
   const { saveData: saveFareChanges } = useSave();
   const {
      create: createNewDistributionGroupNumber,
      createApiResponseData: newDistributionGroupNumberResponse,
   } = useCreateApi();
   const { fetchData: validateDistributionGroupNumber } = useFetch();
   const [distributionGroupNumber, setDistributionGroupNumber] = useState(
      newDistributionGroupNumberResponse?.dgNumber
   );
   const {
      isLoading: isSpecifiedFaresLoading,
      responseData: responseSpecifiedFaresData,
      searchStatus,
   } = useSearch({
      searchQuery: productType.isSpecifiedFare ? specifiedFaresSearchQuery : [],
      abortSearch,
      responseKeyName: API_RESPONSE_KEY_NAMES.FARES,
      url: productType.isSpecifiedFare ? URL_DYNAMIC_RESOLUTION : '',
   });
   const {
      isLoading: isConstructedFaresLoading,
      responseData: responseConstructedFaresData,
   } = useSearch({
      searchQuery: productType.isConstructedFare
         ? constructedFaresSearchQuery
         : [],
      abortSearch,
      responseKeyName: API_RESPONSE_KEY_NAMES.FARES,
      url: productType.isConstructedFare ? URL_CONSTRUCTED_FARES : '',
   });
   const { user, activeScreen } = useCorePricingContext();
   const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
   const [faresToDelete, setFaresToDelete] = useState([]);
   const [agGridApi, setAgGridApi] = useState(null);
   const hostCarrierCode = getHostCarrierCode();
   const [faresData, setFaresData] = useState([]);
   const [recordCount, setRecordCount] = useState(0);

   const handleSelectedRowsUpdate = (selectedNodes) => {
      const selectedRowIndices = selectedNodes?.map(
         (node) => node.rowIndex + 1
      );
      setSelectedRows(selectedRowIndices);
   };

   const generateDistributionGroupNumber = useCallback(async () => {
      const newDistributionGroupNumberResponse =
         await createNewDistributionGroupNumber(
            URL_NEW_DISTRIBUTION_GROUP_GENERATE
         );

      const newDistributionGroupNumber =
         newDistributionGroupNumberResponse?.dgNumber;

      if (newDistributionGroupNumber) {
         setDistributionGroupNumber(newDistributionGroupNumber);
         return newDistributionGroupNumber;
      } else {
         toastWarn(NOTIFICATION_MESSAGES.DISTRIBUTION_GROUP_GENERATION_FAILED);
         return '';
      }
   }, [createNewDistributionGroupNumber]);

   const handleSave = async (editedData, _, gridApi) => {
      const payload = createSaveFarePayload(editedData);

      if (!distributionGroupNumber) {
         const newDistributionGroupNumber =
            await generateDistributionGroupNumber();

         if (newDistributionGroupNumber) {
            payload.forEach((record) => {
               record.dgNumber = newDistributionGroupNumber;
            });
         }
      } else {
         const validatedDistributionGroupNumberResponse =
            validateDistributionGroupNumber(
               [
                  {
                     key: 'dgNumber',
                     operator: '==',
                     value: distributionGroupNumber.toString(),
                  },
               ],
               URL_NEW_DISTRIBUTION_GROUP_SEARCH
            );

         if (
            !validatedDistributionGroupNumberResponse?.dgNumber ===
            distributionGroupNumber
         ) {
            toastWarn(NOTIFICATION_MESSAGES.DISTRIBUTION_GROUP_DOES_NOT_EXIST);
            return;
         } else {
            payload.forEach((record) => {
               record.dgNumber = distributionGroupNumber;
            });
         }
      }

      const NO_VALIDATION_FIELDS = [
         'routingNumber',
         'fareAmount',
         'footnoteCode',
         'id',
      ];

      for (const record of payload) {
         for (const [key, value] of Object.entries(record)) {
            if (value === null || value === undefined) {
               if (NO_VALIDATION_FIELDS.includes(key)) {
                  delete record[key];
               } else {
                  toastWarn(
                     `Record ${record.rowIndex + 1} - ${
                        NOTIFICATION_MESSAGES.FILL_ALL_MANDATORY_FIELDS
                     }`
                  );
                  return;
               }
            }
         }
      }

      const saveFareChangesResponse = await saveFareChanges(
         payload,
         URL_CORE_PRICING_NEW_FARES_SAVE
      );

      const failedToSaveRecords = saveFareChangesResponse
         .filter(
            (record) => record.status === USER_ACTION_CODE_FARES_SCREEN.ERROR
         )
         .map((record) => {
            return {
               node: gridApi.getRowNode(record.id),
               responseData: record,
            };
         });

      const successfullySavedRecords = saveFareChangesResponse
         .filter((record) => {
            return record.status === FARE_STATUS.WORKING;
         })
         .map((record) => {
            return {
               node: gridApi.getRowNode(record.id),
               responseData: record,
            };
         });

      if (failedToSaveRecords?.length) {
         gridApi.applyTransaction({
            update: failedToSaveRecords?.map((record) => ({
               ...record.node.data,
               ...record.responseData,
               id: record.node.id,
               isError: true,
            })),
         });

         if (failedToSaveRecords.length <= ERROR_MESSAGE_LIMIT) {
            failedToSaveRecords.forEach((record) => {
               toastWarn(
                  `Failed to save record ${record.node.rowIndex} - ${record.responseData.reason}`
               );
            });
         } else {
            const fareErrors = failedToSaveRecords.map(
               // eslint-disable-next-line no-unused-vars
               ({ responseData: { id, ...data } }) => {
                  return { ...data };
               }
            );

            toastWarn(
               <div>
                  <CSVLink data={fareErrors} filename='Error Fares.csv'>
                     Failed to save {failedToSaveRecords.length} records. Please
                     click on this link to download error report
                  </CSVLink>
               </div>
            );
         }
      }

      if (successfullySavedRecords?.length) {
         gridApi.applyTransaction({
            update: successfullySavedRecords?.map((record) => ({
               ...record.node.data,
               ...record.responseData,
               id: record.node.id,
               isError: false,
               isEdited: false,
               isNewRow: false,
               isCancelledRow: false,
            })),
         });

         toastSuccess(
            `Successfully saved ${successfullySavedRecords.length} record${
               successfullySavedRecords.length > 1 ? 's' : ''
            }`
         );
      }
   };

   const deleteFares = (rowsToDelete, gridApi) => {
      const workingFares = rowsToDelete.filter(
         ({ status, dgNumber }) => status === FARE_STATUS.WORKING && !!dgNumber
      );

      if (!workingFares.length) {
         toastWarn(NOTIFICATION_MESSAGES.SELECT_WORKING_FARES_TO_DELETE);
         return;
      }

      setAgGridApi(gridApi);

      const formattedWorkingFares = workingFares.map((record) => {
         const userActionCode = getUserActionCode(record);
         return {
            origin: record.origin,
            destination: record.destination,
            fareClassCode: record.fareClassCode,
            carrierCode: record.carrierCode,
            tariffNumber: record.tariffNumber,
            footnoteCode: record.footnoteCode,
            routingNumber: record.routingNumber,
            roundTripIndicator: record.roundTripIndicator,
            currencyCode: record.currencyCode,
            fareAmount: record.fareAmount,
            userActionCode: userActionCode,
            dgNumber: record.dgNumber,
            id: record.id,
         };
      });

      const apiPayload = {
         screen: SCREEN_NAMES.FARES,
         hostCarrier: hostCarrierCode,
         distributionGroupNumber: {
            carrierCode: hostCarrierCode,
            user: user.userId,
            workingFares: formattedWorkingFares,
         },
      };

      setIsDeleteModalOpen(true);
      setFaresToDelete(apiPayload);
   };

   const handleDeleteModalClose = () => setIsDeleteModalOpen(false);

   const onDeleteSuccess = () => {
      setIsDeleteModalOpen(false);
      toastWarn(NOTIFICATION_MESSAGES.RECORD_DELETE_SUCCESS);
      agGridApi?.applyTransaction({
         remove: faresToDelete.distributionGroupNumber.workingFares,
      });
   };

   const updateEffectiveDate = (newRows, gridApi) => {
      const temporaryFares = newRows.map((record) => ({
         ...record,
         userActionCode: USER_ACTION_CODE_FARES_SCREEN.NEW,
         effectiveDate: DateUtil.formatDate(
            new Date(),
            DateUtil.DATE_FORMATS.DD_MMM_YY
         ).toUpperCase(),
         status: '',
      }));

      gridApi.applyTransaction({
         update: temporaryFares,
      });
   };

   const onModelUpdated = (params) => {
      const displayedRows = params.api.getDisplayedRowCount();
      const selectedRowIndices = params.api
         .getSelectedNodes()
         .map((node) => node.rowIndex + 1);
      setSelectedRows(selectedRowIndices);
      setRecordCount(displayedRows);
   };

   useEffect(() => {
      if (searchStatus === API_STATUS.COMPLETED) {
         toastSuccess(NOTIFICATION_MESSAGES.SEARCH_COMPLETED);
      }
   }, [searchStatus]);

   useEffect(() => {
      if (
         responseConstructedFaresData?.length > 0 &&
         responseSpecifiedFaresData?.length > 0
      ) {
         setFaresData([
            ...responseConstructedFaresData,
            ...responseSpecifiedFaresData,
         ]);
      } else if (responseConstructedFaresData?.length > 0)
         setFaresData([...responseConstructedFaresData]);
      else if (responseSpecifiedFaresData?.length > 0)
         setFaresData([...responseSpecifiedFaresData]);
      else setFaresData([]);
   }, [responseConstructedFaresData, responseSpecifiedFaresData]);

   return (
      <>
         <section className='fares-table'>
            {(isConstructedFaresLoading && isSpecifiedFaresLoading) ||
            isConstructedFaresLoading ||
            isSpecifiedFaresLoading ? (
               <div className='loader'>
                  <Loader size={SIZE.SMALL} />
               </div>
            ) : faresData?.length > 0 ? (
               <>
                  <AgGridReactTable
                     rowData={faresData}
                     headers={
                        productType.isConstructedFare
                           ? constructedFaresTableHeaders
                           : specifiedFaresTableHeaders
                     }
                     onSelectedRowsChange={handleSelectedRowsUpdate}
                     rememberSelectedRow
                     enableMultiRowSelection
                     enableRowIds
                     enableContextMenu
                     contextMenuConfig={{
                        ...contextMenuConfig,
                        onDeleteChanges: deleteFares,
                        onSaveChanges: handleSave,
                        onCancel: cancelFareRecords,
                        onTemplate: updateEffectiveDate,
                        onAddNewRow: updateEffectiveDate,
                     }}
                     rowClassRules={getRowClasses()}
                     appendIncomingRowData
                     onModelUpdated={onModelUpdated}
                  />

                  <div className='spark-flex justify-between'>
                     <div className='record-indicator'>
                        <RecordCount
                           searchStatus={searchStatus}
                           selectedRowIndices={selectedRows}
                           totalRowCount={recordCount}
                        />
                     </div>

                     <NewDistributionGroup
                        distributionGroupNumber={distributionGroupNumber}
                        setDistributionGroupNumber={setDistributionGroupNumber}
                        generateDistributionGroupNumber={
                           generateDistributionGroupNumber
                        }
                     />
                  </div>
               </>
            ) : (
               <p className='spark-bold grid place-items-center h-full text-lg'>
                  No fare results found
               </p>
            )}
         </section>

         <Modal
            onClose={handleDeleteModalClose}
            open={isDeleteModalOpen}
            titleHeadingLevel={3}
            modalId='delete-fares-modal'
            className='delete-fares-modal'
            contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
         >
            <DistributionDeleteConfirmation
               distributionListToDelete={faresToDelete}
               onUpdateDistributionTable={onDeleteSuccess}
               onCancelButtonClick={handleDeleteModalClose}
               selectedScreen={activeScreen}
            />
         </Modal>
      </>
   );
};

FaresResultScreen.propTypes = {
   specifiedFaresSearchQuery: object.isRequired,
   constructedFaresSearchQuery: object.isRequired,
   abortSearch: bool.isRequired,
   productType: object.isRequired,
};

export default FaresResultScreen;
